import { motion } from "framer-motion";

import { AnimatePresence } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Checkbox from "@/components/ui/checkbox";
import { observer } from "mobx-react-lite";
import { NumericParameterBuilderProps } from "./types";
import { handleNumberChange } from "./utils";

export const NumberConstraints = observer(
  ({ inputType, parameter, isViewing }: NumericParameterBuilderProps) => {
    const onBlurMin = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isNaN(Number(e.target.value))) {
        parameter.updateMin(0);
      }

      if (
        parameter.default !== undefined &&
        Number(parameter.default) < Number(e.target.value)
      ) {
        parameter.updateDefault(Number(e.target.value));
      }
    };

    const onBlurMax = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isNaN(Number(e.target.value))) {
        parameter.updateMax(100);
      }

      if (
        parameter.default !== undefined &&
        Number(parameter.default) > Number(e.target.value)
      ) {
        parameter.updateDefault(Number(e.target.value));
      }
    };

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const minValue = e.target.value;
      if (
        parameter.min !== undefined &&
        Number(minValue) >= Number(parameter.max)
      ) {
        parameter.updateMax(Math.floor(Number(minValue) + 1));
      }

      parameter.updateMin(
        handleNumberChange(minValue, parameter.isInt) as number,
      );
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const maxValue = e.target.value;
      if (
        parameter.min !== undefined &&
        Number(parameter.min) >= Number(maxValue)
      ) {
        parameter.updateMin(Math.floor(Number(maxValue) - 1));
      }

      parameter.updateMax(
        handleNumberChange(maxValue, parameter.isInt) as number,
      );
    };

    const handleIsIntChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      parameter.updateIsInt(e.target.checked);

      if (e.target.checked) {
        parameter.updateMin(Math.floor(Number(parameter.min)));
        parameter.updateMax(Math.floor(Number(parameter.max)));
        parameter.updateDefault(Math.floor(Number(parameter.default)));

        if (parameter.step) {
          parameter.updateStep(Math.floor(Number(parameter.step)));
        }
      }
    };

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="number-constraints"
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: inputType === "number" ? 1 : 0,
            height: inputType === "number" ? "auto" : 0,
            padding: inputType === "number" ? "12px" : 0,
          }}
          transition={{
            duration: 0.3,
          }}
          className="space-y-5 overflow-hidden rounded-md bg-gray-50 p-4"
        >
          <h4 className="text-sm font-medium text-gray-700">
            Number Constraints
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="minValueInput" className="text-sm font-medium">
                Min Value
              </Label>
              <Input
                id="minValueInput"
                onBlur={onBlurMin}
                readOnly={isViewing}
                value={(parameter.min as unknown as string) ?? 0}
                onChange={handleMinChange}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxValueInput" className="text-sm font-medium">
                Max Value
              </Label>
              <Input
                id="maxValueInput"
                onBlur={onBlurMax}
                readOnly={isViewing}
                value={(parameter.max as unknown as string) ?? 100}
                onChange={handleMaxChange}
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Checkbox
              id="integerOnly"
              checked={parameter.isInt}
              onChange={handleIsIntChange}
              disabled={isViewing}
            />
            <div>
              <Label htmlFor="integerOnly" className="text-sm font-medium">
                Integer values only
              </Label>
              <p className="text-xs text-gray-500">
                If enabled, only whole numbers will be accepted
              </p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  },
);
